import { Head, useForm, usePage } from "@inertiajs/react"
import { AlertTriangle, ShieldCheck } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Button } from "@/components/ui/button"
import { CheckboxOption } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"
import type { DeliverySettings } from "@/types/automation"

const DESCRIPTION =
  "The app-wide delivery controls: whether automations send real texts, how many a single run may send, and where test sends are redirected."

export default function AdminDeliverySettings({
  settings,
}: {
  settings: DeliverySettings
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    dry_run: settings.dry_run,
    send_ceiling: String(settings.send_ceiling),
    test_recipient: settings.test_recipient ?? "",
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    // Nest explicitly rather than posting flat — Rails' ParamsWrapper keeps
    // only keys that are real columns, and the same trap already bit the
    // automation setup form.
    form.transform((data) => ({ app_setting: data }))
    form.patch("/admin/delivery_settings")
  }

  return (
    <>
      <Head title="Delivery">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Delivery" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Delivery"
          description="What happens when an automation fires. These settings apply to the whole app, not to one District Council."
        />

        <div className="mt-6 space-y-6">
          <FlashNotice />

          <ArmedState settings={settings} />

          <form onSubmit={submit} className="space-y-6">
            <div className="rounded-md border border-hairline bg-page p-4">
              <CheckboxOption
                checked={form.data.dry_run}
                onChange={(event) =>
                  form.setData("dry_run", event.target.checked)
                }
                label={
                  <span className="font-medium text-ink-display">
                    Rehearsal — record everything, send nothing
                  </span>
                }
                description="Runs still query the roster, apply targeting and opt-outs, and write a record for every member. They just never dial Unite. Leave this on unless you are deliberately sending."
              />
            </div>

            <div>
              <label htmlFor="send_ceiling">Send ceiling</label>
              <Input
                id="send_ceiling"
                type="number"
                min={1}
                className="mt-1 max-w-40"
                value={form.data.send_ceiling}
                onChange={(event) =>
                  form.setData("send_ceiling", event.target.value)
                }
              />
              <p className="mt-1 max-w-prose text-xs text-ink-muted">
                A real run that would text more members than this is refused
                before anything is sent, and the reason is recorded on the run.
                Rehearsals ignore the ceiling — rehearsing the whole audience is
                what they are for.
              </p>
              {errors.send_ceiling && (
                <p className="mt-1 text-xs text-danger-display">
                  {errors.send_ceiling}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="test_recipient">Test recipient</label>
              <Input
                id="test_recipient"
                className="mt-1 max-w-64"
                placeholder="+12025550199"
                value={form.data.test_recipient}
                onChange={(event) =>
                  form.setData("test_recipient", event.target.value)
                }
              />
              <p className="mt-1 max-w-prose text-xs text-ink-muted">
                When set, every real send is addressed here instead of the
                member's own number, and the run records that it was redirected.
                This is how a real send is proven end to end without texting a
                member. Leave blank to text members at their own numbers.
              </p>
              {errors.test_recipient && (
                <p className="mt-1 text-xs text-danger-display">
                  {errors.test_recipient}
                </p>
              )}
            </div>

            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Saving…" : "Save"}
            </Button>
          </form>

          {settings.updated_by && settings.updated_at && (
            <p className="text-xs text-ink-muted">
              Last changed by {settings.updated_by} on{" "}
              {new Date(settings.updated_at).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
              .
            </p>
          )}
        </div>
      </AdminShell>
    </>
  )
}

/**
 * What is actually in force, which is not always what the switch says: the
 * environment can pin dry-run on, and missing Unite credentials force it too.
 * Someone deciding whether it is safe to press "Run now" needs the real answer,
 * not the stored one.
 */
function ArmedState({ settings }: { settings: DeliverySettings }) {
  if (settings.effective_dry_run) {
    const reason = settings.forced_dry_run
      ? "SMS_DRY_RUN is set in this environment, so Rehearsal cannot be turned off here."
      : !settings.unite_configured
        ? "There are no Unite credentials in this environment, so nothing could be sent even with the switch off."
        : "Automations will record exactly who would have been texted and send nothing."

    return (
      <div className="callout callout-accent">
        <p className="flex items-start gap-2 text-sm">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Rehearsal is on.</strong> {reason}
          </span>
        </p>
      </div>
    )
  }

  return (
    <div className="callout callout-danger">
      <p className="flex items-start gap-2 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          <strong>Rehearsal is off — automations will send real texts.</strong>{" "}
          Up to {settings.send_ceiling.toLocaleString()} per run
          {settings.test_recipient
            ? `, all redirected to ${settings.test_recipient}.`
            : ", each to the member's own number."}
        </span>
      </p>
    </div>
  )
}
